import { FC } from 'react';
import { Link } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import IconButton from 'components/_core/IconButton';
import Modal from 'components/_core/Modal';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineModal from 'components/_core/SunshineModal';
import RichText from 'components/RichText';

import LogoPreviewCard from '../../../LogoPreviewCard';
import PreviewImage from '../PreviewImage';
import UploadErrorCallout from '../UploadErrorCallout';
import locales from './locales';

const HELP_ARTICLE_LINK = 'https://help.shine.fr/fr/articles/7127637';

interface SubmitStepProps {
  hasUploadError?: boolean;
  isLoading: boolean;
  logoUrl: string;
  fileName: string;
  onGoBack: () => void;
  onSubmit: () => Promise<void>;
}

const SubmitStep: FC<SubmitStepProps> = ({
  fileName,
  hasUploadError,
  isLoading,
  logoUrl,
  onGoBack,
  onSubmit,
}) => {
  const submitText = hasUploadError ? locales.retry : locales.submit;

  return (
    <>
      <PreviewImage isError={hasUploadError} logoUrl={logoUrl} />

      <SunshineModal.Body display="grid">
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>

        <RichText marginY="space-8">{locales.description}</RichText>

        <Link
          alignItems="center"
          href={HELP_ARTICLE_LINK}
          isExternal
          marginBottom="space-8"
          width="fit-content"
        >
          {locales.helpLink}

          <SunshineIcon marginLeft="space-8" name="link" />
        </Link>

        <LogoPreviewCard fileName={fileName} logoUrl={logoUrl} width="full">
          <IconButton
            aria-label={locales.removeFileAriaLabel}
            icon="nav-close"
            onClick={onGoBack}
            variant="tertiary"
          />
        </LogoPreviewCard>

        {hasUploadError ? (
          <UploadErrorCallout errorCode="connectionError" marginTop="space-8" />
        ) : null}
      </SunshineModal.Body>

      <Modal.Footer justifyContent="space-between" marginTop="space-32">
        <Button
          icon="arrow-left"
          iconPosition="left"
          isDisabled={isLoading}
          onClick={onGoBack}
          variant="inline-primary"
        >
          {locales.back}
        </Button>

        <Button
          icon={hasUploadError ? 'switch' : undefined}
          isLoading={isLoading}
          loadingText={submitText}
          onClick={onSubmit}
          spinnerPlacement="end"
        >
          {submitText}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default SubmitStep;
